import { apiGet } from './auth'

/** One shop's row in the live General Overview. */
export interface OverviewStore {
  store_id: number
  store_code: string
  store_name: string
  sales_period_eur: number | null
  sales_today_eur: number | null
  grid_hours: number | null
  grid_hours_per_day: number | null
  grid_status: 'matched' | 'below_minimum' | 'above_maximum' | null
  /** Worked hours: timesheets for completed days, elapsed roster for today. */
  worked_hours: number | null
  /** Shiftbase labour cost for the period; null below assistant manager. */
  col_eur: number | null
  /** Cost ÷ period sales for this shop; null when either figure is missing. */
  col_pct: number | null
  revenue_available: boolean
  /**
   * Where `sales_period_eur` came from: the VM Hub archive, the live vendor, or both
   * sides of the shop's cutover. Null when period sales were not requested.
   */
  period_sales_source: 'butlaroo' | 'vmhub_history' | 'mixed' | null
  error_code: string | null
  error_message: string | null
  hours_error_code: string | null
  hours_error_message: string | null
}

export interface OverviewFeatures {
  /** Period sales; false for team members and shift leaders. */
  period_sales: boolean
  /** COL grid hours; false for team members and shift leaders. */
  grid_hours: boolean
  /** Actual Shiftbase hours + grid variance; false below assistant manager. */
  worked_hours: boolean
  /** COL% card; false below assistant manager. */
  col_pct: boolean
  /** COS% card (placeholder figure); false below assistant manager. */
  cos_pct: boolean
}

export interface OverviewTotals {
  sales_period_eur: number | null
  sales_today_eur: number
  grid_hours: number | null
  /** Sum of actual worked hours across shops that supplied Shiftbase data. */
  worked_hours: number | null
  /**
   * Worked minus grid hours, computed only across shops with **both** figures.
   * Negative means the shops worked below their grid (green in the UI).
   */
  grid_variance: number | null
  /** Shops that supplied actual Shiftbase hours. */
  hours_stores: number
  /** Shops included in the grid-variance comparison. */
  variance_stores: number
  /** Labour cost across shops that supplied both cost and sales. */
  col_eur: number | null
  /**
   * Cost ÷ sales across shops with both figures — the same arithmetic the
   * 4-Wall Analysis shows, so the two screens always agree.
   */
  col_pct: number | null
  /** Shops included in the COL% calculation. */
  col_stores: number
  /**
   * True only when every requested shop supplied every figure. A partial total
   * is still useful, but must never be presented as the whole company.
   */
  complete: boolean
}

/** Response of `GET /api/v1/overview/general`. */
export interface GeneralOverview {
  start_date: string
  end_date: string
  today: string
  currency: string
  vat_included: boolean
  features: OverviewFeatures
  stores_requested: number
  stores_with_sales: number
  stores: OverviewStore[]
  totals: OverviewTotals
  warnings: string[]
  /** Aggregate origin of the period figures; null when period sales are hidden. */
  period_source: 'butlaroo' | 'vmhub_history' | 'mixed' | null
  source: 'butlaroo_insights' | 'vmhub_history' | 'mixed'
  grid_source: 'col_labor_grid'
  aggregation: 'vendor_blackbox' | 'stored_aggregates' | 'mixed'
  fetched_at: string
}

export interface OverviewParams {
  from: string
  to: string
  /** Omit to let the server use the account's whole entitled scope. */
  storeIds?: number[]
  /** TanStack Query passes this so a superseded period aborts its fetch. */
  signal?: AbortSignal
}

/**
 * Live General Overview: period sales, today's sales, COL grid hours and
 * (assistant manager+) actual Shiftbase worked hours plus COL%. COS% has no feed
 * yet, so its card is a visible placeholder on the same screens.
 *
 * The server enforces store scope and role visibility; this client only sends
 * the selected dates and shops.
 */
export function fetchGeneralOverview({
  from,
  to,
  storeIds,
  signal,
}: OverviewParams): Promise<GeneralOverview> {
  return apiGet<GeneralOverview>(
    '/overview/general',
    {
      start_date: from,
      end_date: to,
      store_ids: storeIds && storeIds.length > 0 ? storeIds : undefined,
    },
    signal,
  )
}
